<script setup>
import { computed, ref, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { getPointerViewportPosition } from 'dashboard/helper/dashboardZoom';
import {
  dynamicTime,
  dynamicTimeLocalized,
  shortTimestamp,
} from 'shared/helpers/timeHelper';
import {
  snoozedReopenTimeToTimestamp,
  shortenSnoozeTime,
} from 'dashboard/helper/snoozeHelpers';
import { NOTIFICATION_TYPES_MAPPING } from 'dashboard/routes/dashboard/inbox/helpers/InboxViewHelpers';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import SLACardLabel from 'dashboard/components-next/Conversation/ConversationCard/SLACardLabel.vue';
import InboxContextMenu from 'dashboard/routes/dashboard/inbox/components/InboxContextMenu.vue';

const props = defineProps({
  inboxItem: { type: Object, default: () => ({}) },
  stateInbox: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
  'click',
  'contextMenuOpen',
  'contextMenuClose',
  'markNotificationAsRead',
  'markNotificationAsUnRead',
  'deleteNotification',
]);

const { t } = useI18n();

const isContextMenuOpen = ref(false);
const contextMenuPosition = ref({ x: null, y: null });
const slaCardLabel = ref(null);

const getMessageClasses = {
  emphasis: 'text-sm font-medium text-n-slate-11',
  emphasisUnread: 'text-sm font-medium text-n-slate-12',
  normal: 'text-sm font-normal text-n-slate-11',
  normalUnread: 'text-sm text-n-slate-12',
};

const primaryActor = computed(() => props.inboxItem?.primaryActor);
const meta = computed(() => primaryActor.value?.meta);
const assigneeMeta = computed(() => meta.value?.sender);
const canManageNotification = computed(
  () => !props.inboxItem?.disableNotificationActions
);
const isUnread = computed(() => !props.inboxItem?.readAt);
const inbox = computed(() => props.stateInbox);
const isEmailInbox = computed(
  () => inbox.value?.channelType === 'Channel::Email'
);

const emailSubject = computed(() => {
  if (!isEmailInbox.value) return '';

  return (
    primaryActor.value?.additionalAttributes?.mail_subject ||
    primaryActor.value?.messages?.[0]?.contentAttributes?.email?.subject ||
    ''
  );
});

const emailSenderName = computed(() => {
  if (!isEmailInbox.value) return '';

  return (
    assigneeMeta.value?.name ||
    primaryActor.value?.contact?.name ||
    assigneeMeta.value?.email ||
    'Remetente desconhecido'
  );
});

const emailSenderEmail = computed(() => {
  if (!isEmailInbox.value) return '';

  return assigneeMeta.value?.email || primaryActor.value?.contact?.email || '';
});

const emailSenderAddress = computed(() =>
  emailSenderEmail.value ? `<${emailSenderEmail.value}>` : ''
);

const emailSnippet = computed(() => {
  if (!isEmailInbox.value) return '';

  return (
    props.inboxItem?.pushMessageBody ||
    primaryActor.value?.lastMessage?.content ||
    ''
  );
});

const inboxIcon = computed(() => {
  const { channelType, medium } = inbox.value || {};
  return getInboxIconByType(channelType, medium);
});

const hasSlaThreshold = computed(() => {
  return slaCardLabel.value?.hasSlaThreshold && primaryActor.value?.slaPolicyId;
});

const lastActivityAt = computed(() => {
  const timestamp = props.inboxItem?.lastActivityAt;
  return timestamp ? dynamicTimeLocalized(timestamp) : '';
});

const menuItems = computed(() => [
  {
    key: isUnread.value ? 'mark_as_read' : 'mark_as_unread',
    icon: isUnread.value ? 'mail' : 'mail-unread',
    label: t(`INBOX.MENU_ITEM.MARK_AS_${isUnread.value ? 'READ' : 'UNREAD'}`),
  },
  { key: 'delete', icon: 'delete', label: t('INBOX.MENU_ITEM.DELETE') },
]);

const messageClasses = computed(() => ({
  emphasis: isUnread.value
    ? getMessageClasses.emphasisUnread
    : getMessageClasses.emphasis,
  normal: isUnread.value
    ? getMessageClasses.normalUnread
    : getMessageClasses.normal,
}));

const formatPushMessage = message => {
  if (message.startsWith(': ')) {
    return message.slice(2);
  }

  return message.replace(/^([^:]+):/g, (match, name) => {
    return `<span class="${messageClasses.value.emphasis}">${name}:</span>`;
  });
};

const formattedMessage = computed(() => {
  return `<span class="${messageClasses.value.normal}">${formatPushMessage(props.inboxItem?.pushMessageBody || '')}</span>`;
});

const notificationDetails = computed(() => {
  const type = props.inboxItem?.notificationType?.toUpperCase() || '';
  const [icon = '', color = 'text-n-blue-11'] =
    NOTIFICATION_TYPES_MAPPING[type] || [];
  return { text: type ? t(`INBOX.TYPES_NEXT.${type}`) : '', icon, color };
});

const actorName = computed(() => {
  if (isEmailInbox.value) return emailSenderName.value;

  return (
    assigneeMeta.value?.name ||
    primaryActor.value?.contact?.name ||
    inbox.value?.name ||
    t('INBOX.CENTER.ACTIVITY_TITLE')
  );
});

const notificationTitle = computed(() => {
  if (isEmailInbox.value) {
    return emailSubject.value || t('INBOX.EMAIL.NO_SUBJECT');
  }

  return (
    props.inboxItem?.pushMessageTitle ||
    notificationDetails.value.text ||
    t('INBOX.CENTER.ACTIVITY_TITLE')
  );
});

const notificationBody = computed(() => {
  if (isEmailInbox.value) {
    return emailSnippet.value || t('INBOX.NO_CONTENT');
  }

  return props.inboxItem?.pushMessageBody || t('INBOX.NO_CONTENT');
});

const snoozedUntilTime = computed(() => {
  const { snoozedUntil } = props.inboxItem;
  if (!snoozedUntil) return null;
  return shortenSnoozeTime(
    dynamicTime(snoozedReopenTimeToTimestamp(snoozedUntil))
  );
});

const hasLastSnoozed = computed(() => props.inboxItem?.meta?.lastSnoozedAt);

const snoozedText = computed(() => {
  return !hasLastSnoozed.value
    ? t('INBOX.TYPES_NEXT.SNOOZED_UNTIL', {
        time: shortTimestamp(snoozedUntilTime.value),
      })
    : t('INBOX.TYPES_NEXT.SNOOZED_ENDS');
});

const contextMenuActions = {
  close: () => {
    isContextMenuOpen.value = false;
    contextMenuPosition.value = { x: null, y: null };
    emit('contextMenuClose');
  },
  open: e => {
    e.preventDefault();
    // Aceleres: ponto da VIEWPORT — o menu e `position: fixed`. Ver
    // helper/dashboardZoom.js e o mesmo ajuste em ConversationCard.vue.
    contextMenuPosition.value = getPointerViewportPosition(e);
    isContextMenuOpen.value = true;
    emit('contextMenuOpen');
  },
  handle: key => {
    const actions = {
      mark_as_read: () => emit('markNotificationAsRead', props.inboxItem),
      mark_as_unread: () => emit('markNotificationAsUnRead', props.inboxItem),
      delete: () => emit('deleteNotification', props.inboxItem),
    };
    actions[key]?.();
  },
};

onBeforeMount(contextMenuActions.close);
</script>

<template>
  <div
    role="button"
    class="group relative flex w-full cursor-pointer flex-col rounded-xl border border-n-weak px-3 py-2.5 transition-colors hover:border-n-slate-5 hover:shadow-sm md:px-4 md:py-3"
    :class="isUnread ? 'bg-[#F6FBF7]' : 'bg-white'"
    @contextmenu="canManageNotification && contextMenuActions.open($event)"
    @click="emit('click')"
  >
    <div class="flex min-w-0 items-start gap-2.5">
      <span
        v-if="isUnread"
        class="mt-3.5 size-2 shrink-0 rounded-full bg-[#15803D]"
        aria-hidden="true"
      />
      <span v-else class="w-2 shrink-0" aria-hidden="true" />
      <Avatar
        :name="actorName"
        :src="assigneeMeta?.thumbnail"
        :size="36"
        rounded-full
        class="mt-0.5 shrink-0"
      />
      <div class="min-w-0 flex-1">
        <div class="flex min-w-0 items-start justify-between gap-3">
          <div class="flex min-w-0 items-center gap-1.5">
            <span
              class="truncate text-[13px]"
              :class="
                isUnread
                  ? 'font-bold text-n-slate-12'
                  : 'font-normal text-n-slate-12'
              "
            >
              {{ actorName }}
            </span>
            <span
              v-if="isEmailInbox && emailSenderAddress"
              class="hidden truncate text-xs text-n-slate-10 sm:inline"
            >
              {{ emailSenderAddress }}
            </span>
          </div>
          <time
            class="shrink-0 text-[11px]"
            :class="
              isUnread ? 'font-semibold text-n-slate-12' : 'text-n-slate-10'
            "
          >
            {{ lastActivityAt }}
          </time>
        </div>
        <p
          class="mt-0.5 truncate text-[13px]"
          :class="
            isUnread
              ? 'font-bold text-n-slate-12'
              : 'font-normal text-n-slate-12'
          "
        >
          {{ notificationTitle }}
        </p>
        <p
          v-if="isEmailInbox"
          class="mt-0.5 line-clamp-1 text-xs leading-4 text-n-slate-11 md:text-[13px]"
        >
          {{ notificationBody }}
        </p>
        <p
          v-else
          v-dompurify-html="formattedMessage"
          v-twemoji
          class="mt-0.5 line-clamp-1 text-xs leading-4 md:text-[13px]"
        />
        <div
          class="mt-1.5 flex min-w-0 flex-wrap items-center justify-between gap-x-2 gap-y-1"
        >
          <div
            class="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px]"
          >
            <span
              v-if="snoozedUntilTime || hasLastSnoozed"
              class="inline-flex max-w-full items-center gap-1.5 font-medium text-n-blue-11"
            >
              <Icon
                :icon="
                  !hasLastSnoozed
                    ? 'i-lucide-alarm-clock-plus'
                    : 'i-lucide-alarm-clock-off'
                "
                class="size-3 shrink-0"
              />
              <span class="truncate">{{ snoozedText }}</span>
            </span>
            <span
              v-else-if="notificationDetails.text"
              class="inline-flex max-w-full items-center gap-1.5 font-medium"
              :class="isUnread ? notificationDetails.color : 'text-n-slate-10'"
            >
              <Icon :icon="notificationDetails.icon" class="size-3 shrink-0" />
              <span class="truncate">{{ notificationDetails.text }}</span>
            </span>
            <span
              v-if="isEmailInbox && inbox?.name"
              class="inline-flex max-w-40 items-center gap-1 text-n-slate-10"
              :title="inbox.name"
            >
              <Icon
                v-if="inboxIcon"
                :icon="inboxIcon"
                class="size-3 shrink-0"
              />
              <span class="truncate">{{ inbox.name }}</span>
            </span>
            <SLACardLabel
              v-show="hasSlaThreshold"
              ref="slaCardLabel"
              :conversation="primaryActor"
              class="[&>span]:text-xs"
              :class="
                !isUnread &&
                '[&>span]:text-n-slate-11 [&>div>svg]:fill-n-slate-11'
              "
            />
            <CardPriorityIcon
              v-if="primaryActor?.priority"
              :priority="primaryActor.priority"
              class="[&>svg]:size-4"
            />
          </div>
          <div
            v-if="canManageNotification"
            class="ml-auto flex shrink-0 items-center gap-1.5"
          >
            <Button
              type="button"
              color="teal"
              size="xs"
              :label="menuItems[0].label"
              :aria-label="menuItems[0].label"
              :title="menuItems[0].label"
              class="!h-7 !rounded-lg !px-2.5 !text-[11px]"
              @click.stop="contextMenuActions.handle(menuItems[0].key)"
            />
            <Button
              type="button"
              color="ruby"
              size="xs"
              :label="t('INBOX.MENU_ITEM.DELETE')"
              :aria-label="t('INBOX.MENU_ITEM.DELETE')"
              :title="t('INBOX.MENU_ITEM.DELETE')"
              class="!h-7 !rounded-lg !px-2.5 !text-[11px]"
              @click.stop="contextMenuActions.handle('delete')"
            />
          </div>
        </div>
      </div>
    </div>
    <InboxContextMenu
      v-if="isContextMenuOpen && canManageNotification"
      :context-menu-position="contextMenuPosition"
      :menu-items="menuItems"
      @close="contextMenuActions.close"
      @select-action="contextMenuActions.handle"
    />
  </div>
</template>
